import React, { useEffect } from "react"
import { Box, Skeleton, styled } from "@mui/material"

import { colors } from "theme"
import { useAppDispatch, useAppSelector } from "redux/hooks"
import { fetchProducts } from "redux/features/product/productThunk"
import {
  selectFetchCollectionStatus,
  selectProducts,
  selectTotal,
} from "redux/features/product/productSlice"
import LoadableCollection from "components/ui/LoadableCollection"
import { ProductListType } from "types"

export function withProducts<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const WithProducts = (props: GenericType) => {
    const dispatch = useAppDispatch()

    const total = useAppSelector((state) => selectTotal(state))
    const products = useAppSelector((state) =>
      selectProducts(state),
    ) as ProductListType
    const fetchStatus = useAppSelector((state) =>
      selectFetchCollectionStatus(state),
    )

    const handleFetchProducts = () => {
      if (!fetchStatus.isFetching) {
        dispatch(fetchProducts())
      }
    }

    useEffect(() => handleFetchProducts(), [])

    const renderSkeleton = () => {
      return (
        <Container>
          <Card sx={{ height: 250 }} animation="pulse" variant="rectangular" />
        </Container>
      )
    }

    return (
      <LoadableCollection
        fetchStatus={fetchStatus}
        renderLoading={renderSkeleton}
      >
        {() => (
          <WrappedComponent
            {...props}
            total={total}
            products={products}
            fetchStatus={fetchStatus}
            fetchDebts={handleFetchProducts}
          />
        )}
      </LoadableCollection>
    )
  }

  return WithProducts
}

const Container = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin: 42px 0 0 0;
`

const Card = styled(Skeleton)`
  background-color: ${colors.cultured};
  box-shadow: 12px 14px 23px -2px rgb(0 0 0 / 3%);
  border-radius: 26px;
  margin-bottom: 16px;
  padding: 58px;
  width: 100%;
`
